import { createContext, useContext, type ReactNode } from "react";
import type { SeoPage, SeoSitemapEntry } from "./seo-engine";

export interface SeoInitialPayload {
  slug?: string;
  page?: SeoPage;
  sitemap?: SeoSitemapEntry[];
  siteOrigin?: string;
  /** Bare district slugs (without `riads-` prefix) that have a dedicated
   *  district page in this build. Embedded into every prerendered page so the
   *  client-side CTA builder can route property pages to district landings
   *  without depending on a hardcoded list staying in sync. */
  districtSlugs?: string[];
  /** React Query dehydrated cache (`DehydratedState` from @tanstack/react-query).
   *  Populated by the prerender for SEO-critical routes so the client mounts
   *  with real data already in cache instead of fetching from scratch. */
  dehydratedState?: unknown;
  /** AI-generated verdict map: `{ [propertyId]: { [locale]: verdictString } }`.
   *  Embedded for property detail pages (all 6 locales for that property) and
   *  homepage (featured deal for the current locale). Provides a synchronous
   *  window-read fallback alongside the React Query dehydrated cache path. */
  verdicts?: Record<number, Record<string, string>>;
}

const SeoInitialContext = createContext<SeoInitialPayload | null>(null);

export function SeoInitialProvider({
  value,
  children,
}: {
  value: SeoInitialPayload | null;
  children: ReactNode;
}) {
  return (
    <SeoInitialContext.Provider value={value}>{children}</SeoInitialContext.Provider>
  );
}

export function useSeoInitial(): SeoInitialPayload | null {
  return useContext(SeoInitialContext);
}

declare global {
  interface Window {
    __SEO_INITIAL__?: SeoInitialPayload;
  }
}

// Read the build-time injected payload from the prerendered HTML.
export function readWindowSeoInitial(): SeoInitialPayload | null {
  if (typeof window === "undefined") return null;
  return window.__SEO_INITIAL__ ?? null;
}

export const SITE_ORIGIN =
  import.meta.env?.VITE_SITE_ORIGIN || "https://riadintel.com";

export const WHATSAPP_NUMBER =
  import.meta.env?.VITE_WHATSAPP_NUMBER || "+212600000000";

export function whatsappLink(message: string): string {
  const num = WHATSAPP_NUMBER.replace(/[^\d]/g, "");
  return `https://wa.me/${num}?text=${encodeURIComponent(message)}`;
}
